/**
 * BankSlip API Service
 * Frontend service for individual BankSlip payment operations
 */

// ============================================================================
// ERROR HANDLING
// ============================================================================
import { getAPIN8N, N8N_UUID_PATH_BANKING } from "./n8nApiConfig";

export class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

export interface APIResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface BankSlipFormData {
  digitalLine: string;
  amount: string;
  paymentDate: string;
  costCenter: string;
  description: string;
  selectedFavorito?: any;
}

export interface BankSlipPayment {
  id: string;
  uploadId: string;
  recipientName: string;
  recipientDocument: string;
  issuer: string;
  digitalLine: string;
  amount: number;
  paymentDate: string;
  dueDate?: string;
  paymentStatus:
    | "pending"
    | "approved"
    | "rejected"
    | "paid"
    | "cancelled"
    | "scheduled"
    | "processing"
    | "completed"
    | "returned"
    | "canceled"
    | "failed"
    | "expired"
    | "refunded"
    | "requested"
    | "pending_approval";
  information?: string;
  approvedBy?: string;
  approvalDate?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  rejectionDate?: string;
  submissionStatus?: string;
  externalPaymentId?: string;
  createdAt: string;
  upload?: {
    id: string;
    fileName: string;
    processingDate: string;
  };
}

export interface BankSlipPaymentDetails extends BankSlipPayment {
  beneficiaryDocument?: string;
  fullAddress?: string;
  phone?: string;
  email?: string;
  submissionMessage?: string;
  paymentConfirmedAt?: string;
  paymentConfirmedAmount?: number;
  updatedAt: string;
  nominalValue?: number;
  interestFine?: number;
  discounts?: number;
  bank?: string;
  credit_bank_ispb?: string;
}

export interface BankSlipSubmissionResponse {
  uploadId: string;
  paymentId: string;
  amount: number;
  recipient: string;
  digitalLine: string;
  status: string;
  createdAt: string;
}

export interface BankSlipStats {
  totalUploads: number;
  totalPayments: number;
  totalAmount: number;
  paymentsByStatus: {
    pending: { count: number; amount: number };
    approved: { count: number; amount: number };
    rejected: { count: number; amount: number };
    paid: { count: number; amount: number };
    cancelled: { count: number; amount: number };
  };
}

// ============================================================================
// API BASE CONFIGURATION
// ============================================================================

const API_BASE_URL =
  import.meta.env.VITE_N8N_BANKSLIP_API_URL || "http://localhost:3000/api";
const BANKSLIP_API_URL = `${API_BASE_URL}/bankslip`;

/**
 * Make authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<APIResponse<T>> {
  try {
    const url = endpoint.startsWith("http")
      ? endpoint
      : `${BANKSLIP_API_URL}${endpoint}`;

    // Append Authorization header when token exists
    let authHeaders: Record<string, string> = {};
    try {
      const token = localStorage.getItem("barte-auth-token");
      if (token) {
        authHeaders = { Authorization: `Bearer ${token}` };
      }
    } catch {
      // ignore storage errors
    }

    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    });

    const data = await response.json();

    if (!response.ok) {
      console.error(
        `[BANKSLIP_API_SERVICE] API Error (${response.status}):`,
        data
      );
      throw new APIError(
        data.message || `HTTP ${response.status}`,
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    console.error("[BANKSLIP_API_SERVICE] Request failed:", error);

    if (error instanceof APIError) {
      throw error;
    }

    throw new APIError(
      error instanceof Error ? error.message : "Network error",
      0,
      error
    );
  }
}

// ============================================================================
// BANKSLIP PAYMENT OPERATIONS
// ============================================================================

/**
 * Submit individual BankSlip payment
 */
export async function submitBankSlipPayment(
  formData: BankSlipFormData
): Promise<APIResponse<BankSlipSubmissionResponse>> {
  console.log("[BANKSLIP_API_SERVICE] Submitting BankSlip payment:", formData);

  return apiRequest<BankSlipSubmissionResponse>("/submit", {
    method: "POST",
    body: JSON.stringify(formData),
  });
}

/**
 * Approve BankSlip payment
 */
export async function approveBankSlipPayment(
  paymentIdOrIds: string | string[],
  approvedBy: string
): Promise<APIResponse<any>> {
  const paymentIds = Array.isArray(paymentIdOrIds)
    ? paymentIdOrIds
    : [paymentIdOrIds];

  if (!paymentIds.length) {
    throw new APIError("Nenhum pagamento informado para aprovação", 400);
  }

  if (paymentIds.length === 1) {
    return apiRequest<any>(`/approve/${paymentIds[0]}`, {
      method: "POST",
      body: JSON.stringify({ approvedBy }),
    });
  }

  return approveBankSlipPayments(paymentIds, approvedBy);
}

export async function approveBankSlipPayments(
  paymentIds: string[],
  approvedBy: string
): Promise<APIResponse<any>> {
  if (!Array.isArray(paymentIds) || paymentIds.length === 0) {
    throw new APIError(
      "Nenhum pagamento informado para aprovação em lote",
      400
    );
  }

  return apiRequest<any>(`/approve`, {
    method: "POST",
    body: JSON.stringify({ paymentIds, approvedBy }),
  });
}

/**
 * Reject BankSlip payment
 */
export async function rejectBankSlipPayment(
  paymentId: string,
  rejectedBy: string,
  reason: string
): Promise<APIResponse<any>> {
  console.log(
    `[BANKSLIP_API_SERVICE] Rejecting BankSlip payment: ${paymentId}`
  );

  return apiRequest<any>(`/reject/${paymentId}`, {
    method: "POST",
    body: JSON.stringify({ rejectedBy, reason }),
  });
}

// ============================================================================
// BANKSLIP PAYMENT QUERIES
// ============================================================================

/**
 * Get list of BankSlip payments
 */
export async function getBankSlipPayments(
  status?: string,
  limit: number = 50
): Promise<APIResponse<BankSlipPayment[]>> {
  console.log("[BANKSLIP_API_SERVICE] Fetching BankSlip payments");

  const params = new URLSearchParams();
  if (status) params.append("status", status);
  params.append("limit", limit.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/payments?${queryString}` : "/payments";

  return apiRequest<BankSlipPayment[]>(endpoint);
}

export async function cancelBankSlipPayment(uuid: string): Promise<void> {
  const api = getAPIN8N(N8N_UUID_PATH_BANKING);
  await api.delete(`/v1/bank/payments/${uuid}`);
}

/**
 * Get BankSlip payment details
 */
export async function getBankSlipPaymentDetails(
  paymentId: string
): Promise<APIResponse<BankSlipPaymentDetails>> {
  console.log(
    `[BANKSLIP_API_SERVICE] Fetching BankSlip payment details: ${paymentId}`
  );

  return apiRequest<BankSlipPaymentDetails>(`/payments/${paymentId}`);
}

/**
 * Get BankSlip payments statistics
 */
export async function getBankSlipStats(): Promise<APIResponse<BankSlipStats>> {
  console.log("[BANKSLIP_API_SERVICE] Fetching BankSlip statistics");

  return apiRequest<BankSlipStats>("/stats");
}

// ============================================================================
// BANKSLIP UTILITIES
// ============================================================================

/**
 * Validate BankSlip digital line format (client-side validation)
 */
export function validateDigitalLine(digitalLine: string): {
  isValid: boolean;
  errorMessage?: string;
} {
  if (!digitalLine || digitalLine.trim().length === 0) {
    return {
      isValid: false,
      errorMessage: "Linha digitável é obrigatória",
    };
  }

  // Clean the digital line (remove spaces, dots, etc.)
  const cleanLine = digitalLine.replace(/[.\-\s]/g, "");

  // Bank slip digital line should have 47 or 48 digits
  if (!/^\d{47,48}$/.test(cleanLine)) {
    return {
      isValid: false,
      errorMessage: "Linha digitável deve ter 47 ou 48 dígitos",
    };
  }

  return {
    isValid: true,
  };
}

/**
 * Format digital line for display
 */
export function formatDigitalLine(digitalLine: string): string {
  if (!digitalLine) return "";

  // Remove all non-digit characters
  const clean = digitalLine.replace(/\D/g, "");

  // Format as: XXXXX.XXXXX XXXXX.XXXXXX XXXXX.XXXXXX X XXXXXXXXXXXXXXX
  if (clean.length >= 47) {
    return clean.replace(
      /(\d{5})(\d{5})(\d{5})(\d{6})(\d{5})(\d{6})(\d{1})(\d+)/,
      "$1.$2 $3.$4 $5.$6 $7 $8"
    );
  }

  return digitalLine;
}

/**
 * Get payment status label for display
 */
export function getPaymentStatusLabel(status: string): string {
  const normalized = String(status || "").toLowerCase();
  const labels: Record<string, string> = {
    requested: "Solicitado",
    pending: "Pendente",
    pending_approval: "Pendente de Aprovação",
    scheduled: "Agendado",
    approved: "Aprovado",
    rejected: "Rejeitado",
    processing: "Processando",
    paid: "Pago",
    completed: "Pago",
    returned: "Devolvido",
    cancelled: "Cancelado",
    canceled: "Cancelado",
    failed: "Falhou",
    expired: "Expirado",
    refunded: "Reembolsado",
    // Portuguese variants
    cancelado: "Cancelado",
    pendente: "Pendente",
    aprovado: "Aprovado",
    rejeitado: "Rejeitado",
    processando: "Processando",
    pago: "Pago",
  };
  return labels[normalized] || status;
}

/**
 * Get payment status color for UI
 */
export function getPaymentStatusColor(status: string): string {
  const normalized = String(status || "").toLowerCase();
  const colors: Record<string, string> = {
    pending: "hsla(36, 38%, 74%, 0.25)",
    pending_approval: "hsla(36, 38%, 74%, 0.25)",
    scheduled: "hsla(36, 38%, 74%, 0.25)",
    approved: "hsla(223, 80.60%, 26.30%, 0.10)",
    rejected: "hsla(342, 65%, 45%, 0.10)",
    processing: "hsla(278, 67.10%, 31.00%, 0.10)",
    paid: "hsla(223, 67%, 31%, 0.10)",
    completed: "hsla(223, 80.60%, 26.30%, 0.10)",
    returned: "hsla(36, 38%, 74%, 0.25)",
    cancelled: "hsla(0, 0%, 0%, 0.08)",
    canceled: "hsla(0, 0%, 0%, 0.08)",
    failed: "hsla(342, 17%, 72%, 0.25)",
    expired: "hsla(0, 0%, 0%, 0.08)",
    refunded: "hsla(36, 38%, 74%, 0.25)",
  };
  return colors[normalized] || "#6B7280";
}

// Centraliza o tema de cores por status (badge background, texto e ícone) — igual ao PIX
export interface PaymentStatusColors {
  badgeBg: string;
  text: string;
  icon: string;
}

export function getPaymentStatusColors(status: string): PaymentStatusColors {
  const normalized = String(status || "").toLowerCase();
  const defaultText = "hsla(223, 59%, 29%, 1)";

  const map: Record<string, PaymentStatusColors> = {
    pending: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: defaultText,
      icon: defaultText,
    },
    pending_approval: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: "hsla(342, 45%, 27%, 1)",
      icon: "hsla(342, 45%, 27%, 1)",
    },
    scheduled: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: "hsla(342, 45%, 27%, 1)",
      icon: "hsla(342, 45%, 27%, 1)",
    },
    approved: {
      badgeBg: "hsla(223, 80.60%, 26.30%, 0.10)",
      text: "hsla(223, 59%, 29%, 1)",
      icon: "hsla(223, 59%, 29%, 1)",
    },
    paid: {
      badgeBg: "hsla(223, 67%, 31%, 0.10)",
      text: "hsla(223, 59%, 29%, 1)",
      icon: "hsla(223, 59%, 29%, 1)",
    },
    completed: {
      badgeBg: "hsla(223, 80.60%, 26.30%, 0.10)",
      text: "hsla(223, 59%, 29%, 1)",
      icon: "hsla(223, 59%, 29%, 1)",
    },
    processing: {
      badgeBg: "hsla(278, 67.10%, 31.00%, 0.10)",
      text: defaultText,
      icon: defaultText,
    },
    rejected: {
      badgeBg: "hsla(342, 65%, 45%, 0.10)",
      text: "hsla(342, 45%, 27%, 1)",
      icon: "hsla(342, 45%, 27%, 1)",
    },
    failed: {
      badgeBg: "hsla(342, 17%, 72%, 0.25)",
      text: "hsla(342, 45%, 27%, 1)",
      icon: "hsla(342, 45%, 27%, 1)",
    },
    expired: {
      badgeBg: "hsla(0, 0%, 0%, 0.08)",
      text: "hsla(342, 45%, 27%, 1)",
      icon: "hsla(342, 45%, 27%, 1)",
    },
    cancelled: {
      badgeBg: "hsla(0, 0%, 0%, 0.08)",
      text: "hsla(0, 0%, 25%, 1)",
      icon: "hsla(0, 0%, 25%, 1)",
    },
    canceled: {
      badgeBg: "hsla(0, 0%, 0%, 0.08)",
      text: "hsla(0, 0%, 25%, 1)",
      icon: "hsla(0, 0%, 25%, 1)",
    },
    returned: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: "hsla(36, 64%, 29%, 1)",
      icon: "hsla(36, 64%, 29%, 1)",
    },
    refunded: {
      badgeBg: "hsla(36, 38%, 74%, 0.25)",
      text: "hsla(36, 64%, 29%, 1)",
      icon: "hsla(36, 64%, 29%, 1)",
    },
  };

  const colors = map[normalized];
  if (colors) return colors;

  return {
    badgeBg: "hsla(223, 80.60%, 26.30%, 0.10)",
    text: defaultText,
    icon: defaultText,
  };
}

// ============================================================================
// REACT HOOKS FOR BANKSLIP OPERATIONS
// ============================================================================

/**
 * Hook for BankSlip payment submission
 */
export function useSubmitBankSlipPayment() {
  const submitPayment = async (formData: BankSlipFormData) => {
    try {
      const response = await submitBankSlipPayment(formData);
      return response;
    } catch (error) {
      console.error("[BANKSLIP_HOOK] Error submitting payment:", error);
      throw error;
    }
  };

  return { submitPayment };
}

/**
 * Hook for BankSlip payments list
 */
export function useBankSlipPayments(status?: string, limit: number = 50) {
  const fetchPayments = async () => {
    try {
      const response = await getBankSlipPayments(status, limit);
      return response.data || [];
    } catch (error) {
      console.error("[BANKSLIP_HOOK] Error fetching payments:", error);
      throw error;
    }
  };

  return { fetchPayments };
}

/**
 * Hook for BankSlip payment approval/rejection
 */
export function useBankSlipPaymentActions() {
  const approvePayment = async (paymentId: string, approvedBy: string) => {
    try {
      const response = await approveBankSlipPayment(paymentId, approvedBy);
      return response;
    } catch (error) {
      console.error("[BANKSLIP_HOOK] Error approving payment:", error);
      throw error;
    }
  };

  const rejectPayment = async (
    paymentId: string,
    rejectedBy: string,
    reason: string
  ) => {
    try {
      const response = await rejectBankSlipPayment(
        paymentId,
        rejectedBy,
        reason
      );
      return response;
    } catch (error) {
      console.error("[BANKSLIP_HOOK] Error rejecting payment:", error);
      throw error;
    }
  };

  return { approvePayment, rejectPayment };
}
